<template>
  <div class="bg-gray-50 rounded text-xs px-4 py-4 shadow-md">
    <!-- Code and Status Header -->
    <div class="flex items-center justify-between mb-1">
      <div class="flex items-center gap-2 ">
        <span>Dx:</span>
        <span class="px-2 py-1 bg-blue-200 border border-blue-400 text-gray-700 rounded-md  font-semibold">{{ metadata.code }}</span>
        <span :class="getStatusColorClass(metadata.status)"
              class="px-1.5 py-0.5 rounded text-xs border">
          {{ metadata.status }}
        </span>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-2">
        <button
          v-if="!showQAForm && !showCoderResponseForm && !editMode && canQA"
          @click.stop="openQAForm"
          class="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
        >
          QA Review
        </button>
        <button
          v-if="!showQAForm && !showCoderResponseForm && !editMode && canCoderRespond"
          @click.stop="openCoderResponseForm"
          class="px-2 py-0.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
        >
          Coder Response
        </button>
        <button
          v-if="!showQAForm && !editMode && allowEdit"
          @click.stop="toggleEditMode"
          class="btn-primary-sm"
        >
          Edit
        </button>
        <button
          v-if="!showQAForm && !editMode && allowDelete"
          @click.stop="confirmDelete"
          class="btn-danger-sm"
        >
          Delete
        </button>
      </div>
    </div>

    <!-- Short Description -->
    <div v-if="metadata.short_description && !compact" class="text-gray-600 mb-1">
      {{ metadata.short_description }}
    </div>

    <!-- HCC Codes -->
    <div v-if="metadata.model_hcc_array && metadata.model_hcc_array.length > 0" class="flex flex-wrap items-center gap-1 mb-1">
      <span class="text-gray-500">HCC:</span>
      <span v-for="hcc in metadata.model_hcc_array"
            :key="`${hcc.model_num}-${hcc.hcc_code}`"
            class="inline-flex items-center gap-0.5">
        <span class="text-xs font-medium text-gray-600">v{{ hcc.model_num }}:</span>
        <span class="px-1 py-0.5 bg-primary-100 text-primary-700 rounded text-xs border border-primary-200">
          {{ hcc.hcc_code }}
        </span>
      </span>
    </div>

    <!-- Last Billed (Suspect workflow) -->
    <div v-if="metadata.icd_last_billed && !compact" class="text-gray-600 mb-1">
      <span class="text-gray-500">Last Billed:</span>
      {{ metadata.icd_last_billed.clm_thru_dt }} by <span class="sensitive">{{ metadata.icd_last_billed.provider_name }}</span>
      <span v-if="metadata.icd_last_billed.billed_by_practice" class="font-medium">(Patient PCP)</span>
    </div>

    <!-- Edit Mode -->
    <div v-if="editMode" @click.stop class="mt-2 space-y-2">
      <div>
        <label class="block text-xs font-medium text-gray-700 mb-1">Status</label>
        <select
          v-model="editedStatus"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded"
        >
          <option v-for="option in statusOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>

      <div>
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Clinical Evidence
          <span v-if="editRequiresExplanation" class="text-red-500">*</span>
        </label>
        <textarea
          v-model="editedExplanation"
          rows="3"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          :class="{ 'border-red-500': editValidationError }"
        ></textarea>
      </div>

      <div v-if="editValidationError" class="text-xs text-red-600">
        {{ editValidationError }}
      </div>

      <div class="flex justify-end gap-2">
        <button
          @click="cancelEdit"
          class="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="saveEdits"
          :disabled="saving"
          class="btn-primary-sm"
        >
          <font-awesome-icon v-if="saving" :icon="['fas', 'spinner']" class="animate-spin" />
          <span>{{ saving ? 'Saving...' : 'Save' }}</span>
        </button>
      </div>
    </div>

    <!-- Clinical Evidence (normal view) -->
    <div v-else-if="metadata.explanation" class="text-gray-600">
      <span class="text-gray-500">Evidence:</span> {{ metadata.explanation }}
    </div>

    <!-- Uploaded Images Section -->
    <div class="mt-2">
      <!-- Image Uploader - only show if user can edit -->
      <div v-if="allowEdit" class="mb-1">
        <ImageUploader
          :related-object-id="workItem.id"
          :related-object-content-type-model="'work'"
          :disabled="uploadingImage"
          @image-uploaded="addImage"
        />
      </div>

      <!-- Display uploaded images -->
      <div v-if="metadata.uploaded_images && metadata.uploaded_images.length > 0" class="space-y-1">
        <div class="text-xs text-gray-500 font-medium">Attachments:</div>
        <div v-for="image in metadata.uploaded_images"
             :key="image.key"
             class="flex items-center gap-1">
          <MediaViewer
            :can-delete="allowDelete && !uploadingImage"
            :media-key="image.key"
            @media-deleted="removeImage"
          />
        </div>
      </div>
    </div>

    <!-- QA Form Section -->
    <div v-if="showQAForm" @click.stop class="mt-2 bg-white rounded-lg p-3 border border-gray-200">
      <h4 class="font-semibold text-sm text-gray-900 mb-2">QA Review</h4>

      <!-- QA Decision Buttons -->
      <div class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Review Decision
        </label>
        <div class="flex gap-2">
          <button
            @click="setQAResponse(true)"
            :class="[
              'flex-1 py-1 px-2 rounded border text-xs font-medium transition-all',
              qaConcurrence === true
                ? 'bg-green-50 border-green-500 text-green-700'
                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
            ]"
          >
            <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-1" />
            {{ getAgreeText }}
          </button>
          <button
            @click="setQAResponse(false)"
            :class="[
              'flex-1 py-1 px-2 rounded border text-xs font-medium transition-all',
              qaConcurrence === false
                ? 'bg-red-50 border-red-500 text-red-700'
                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
            ]"
          >
            <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-1" />
            {{ getDisagreeText }}
          </button>
        </div>
      </div>

      <!-- Disagreement Reason (Suspect workflow) -->
      <div v-if="qaConcurrence === false && workflowType === 'suspect'" class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Reason <span class="text-red-500">*</span>
        </label>
        <select
          v-model="qaDisagreeReason"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark"
        >
          <option value="">-- Select --</option>
          <option v-for="option in disagreeReasons" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>

      <!-- Status Selection (Encounter workflow disagree) -->
      <div v-if="qaConcurrence === false && workflowType === 'encounter'" class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Status <span class="text-red-500">*</span>
        </label>
        <select
          v-model="qaStatus"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark"
        >
          <option value="">-- Select --</option>
          <option v-for="option in statusOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>

      <!-- QA Notes/Explanation -->
      <div v-if="qaConcurrence === false" class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Explanation
          <span class="text-red-500">*</span>
        </label>
        <textarea
          v-model="qaExplanation"
          rows="2"
          placeholder="Please explain your disagreement..."
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark"
        ></textarea>
      </div>

      <!-- Optional Notes when Agreeing -->
      <div v-if="qaConcurrence === true" class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Notes (optional)
        </label>
        <textarea
          v-model="qaExplanation"
          rows="2"
          placeholder="Additional notes..."
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark"
        ></textarea>
      </div>

      <!-- Error Display -->
      <div v-if="qaErrors.length > 0" class="mb-2">
        <div class="bg-red-50 border border-red-200 rounded p-2">
          <ul class="text-xs text-red-600 list-disc list-inside">
            <li v-for="(error, index) in qaErrors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>

      <!-- Actions - Only show for disagree -->
      <div v-if="qaConcurrence === false" class="flex justify-end gap-2">
        <button
          @click="cancelQA"
          class="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="submitQA"
          :disabled="submitting || !isQAValid"
          class="btn-primary-sm"
        >
          <font-awesome-icon v-if="submitting" :icon="['fas', 'spinner']" class="animate-spin" />
          <span>{{ submitting ? 'Submitting...' : 'Submit' }}</span>
        </button>
      </div>
    </div>

    <!-- Coder Response Form Section -->
    <div v-if="showCoderResponseForm" @click.stop class="mt-2 bg-white rounded-lg p-3 border border-amber-300">
      <h4 class="font-semibold text-sm text-gray-900 mb-2">Coder Response</h4>

      <!-- Decision Buttons -->
      <div class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Response to QA Reviewer
        </label>
        <div class="flex gap-2">
          <button
            @click="setCoderConcurrence(true)"
            :class="[
              'flex-1 py-1 px-2 rounded border text-xs font-medium transition-all',
              coderConcurrence === true
                ? 'bg-green-50 border-green-500 text-green-700'
                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
            ]"
          >
            <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-1" />
            Agree
          </button>
          <button
            @click="setCoderConcurrence(false)"
            :class="[
              'flex-1 py-1 px-2 rounded border text-xs font-medium transition-all',
              coderConcurrence === false
                ? 'bg-red-50 border-red-500 text-red-700'
                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
            ]"
          >
            <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-1" />
            Disagree
          </button>
        </div>
      </div>

      <!-- Notes (required on Disagree) -->
      <div v-if="coderConcurrence !== null" class="mb-3">
        <label class="block text-xs font-medium text-gray-700 mb-1">
          Notes
          <span v-if="coderConcurrence === false" class="text-red-500">*</span>
          <span v-else class="text-gray-500">(optional)</span>
        </label>
        <textarea
          v-model="coderNotes"
          rows="2"
          :placeholder="coderConcurrence === false
            ? 'Explain why you disagree with the QA decision...'
            : 'Additional notes (optional)...'"
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-amber-500"
        ></textarea>
      </div>

      <!-- Error Display -->
      <div v-if="coderErrors.length > 0" class="mb-2">
        <div class="bg-red-50 border border-red-200 rounded p-2">
          <ul class="text-xs text-red-600 list-disc list-inside">
            <li v-for="(error, index) in coderErrors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2">
        <button
          @click="cancelCoderResponse"
          class="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="submitCoderResponse"
          :disabled="submitting || !isCoderResponseValid"
          class="btn-primary-sm"
        >
          <font-awesome-icon v-if="submitting" :icon="['fas', 'spinner']" class="animate-spin" />
          <span>{{ submitting ? 'Submitting...' : 'Submit' }}</span>
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/50" @click="cancelDelete"></div>
      <div class="relative bg-white rounded-lg p-4 max-w-sm mx-4">
        <h3 class="font-semibold text-gray-900 mb-2">Delete Code Suggestion?</h3>
        <p class="text-sm text-gray-600 mb-4">
          This will permanently delete the code suggestion and all associated QA reviews.
        </p>
        <div class="flex justify-end gap-2">
          <button
            @click="cancelDelete"
            class="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="deleteCode"
            :disabled="deleting"
            class="btn-danger-sm"
          >
            <font-awesome-icon v-if="deleting" :icon="['fas', 'spinner']" class="animate-spin" />
            <span>{{ deleting ? 'Deleting...' : 'Delete' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import ImageUploader from '@/components/widgetcomponents/MediaUploader.vue';
import MediaViewer from '@/components/widgetcomponents/MediaViewer.vue';

export default {
  name: 'CodeSuggestionDisplay',
  components: {
    ImageUploader,
    MediaViewer
  },
  props: {
    workItem: {
      type: Object,
      required: true
    },
    taskId: {
      type: Number,
      default: null
    },
    taskTypeId: {
      type: Number,
      required: true
    },
    taskTypeName: {
      type: String,
      default: ''
    },
    compact: {
      type: Boolean,
      default: true
    },
    parentWorkItem: {
      type: Object,
      default: null
    },
    currentUser: {
      type: Object,
      default: null
    },
    children: {
      type: Array,
      default: () => []
    },
    taskMetadata: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['updated'],
  data() {
    return {
      showQAForm: false,
      editMode: false,
      showDeleteConfirm: false,

      editedStatus: '',
      editedExplanation: '',
      editValidationError: '',

      qaConcurrence: null,
      qaDisagreeReason: '',
      qaStatus: '',
      qaExplanation: '',

      showCoderResponseForm: false,
      coderConcurrence: null,
      coderNotes: '',
      coderErrors: [],

      submitting: false,
      saving: false,
      deleting: false,
      uploadingImage: false,
      qaErrors: []
    };
  },
  computed: {
    ...mapGetters('tenant', ['isTenantAdmin', 'isManagerViewer']),

    metadata() {
      return this.workItem.metadata || {};
    },

    workflowType() {
      const name = this.taskTypeName.toLowerCase();
      if (name.includes('claim') || name.includes('episource')) {
        return 'encounter';
      } else if (name.includes('early start')) {
        return 'earlystart';
      } else if (name.includes('suspect') || name.includes('1lr') || name.includes('2lr') || name.includes('3lr') || name.includes('confirm')) {
        return 'suspect';
      }
      return 'encounter';
    },

    isDeleted() {
      return !!this.workItem.deleted_at;
    },

    allowEdit() {
      if (this.isDeleted) return false;
      if (this.workItem.appuser !== this.currentUser?.id) return false;
      return this.taskId == null || this.workItem.task === this.taskId;
    },

    isQAReturned() {
      return this.taskMetadata?.reassignment_type === 'qa_returned';
    },

    activeChildren() {
      // Filter out soft-deleted children — a deleted QA review should not
      // block the reviewer from re-QA'ing the code.
      return (this.children || []).filter(c => !c.deleted_at);
    },

    canQA() {
      // No QA on deleted codes
      if (this.isDeleted) return false;

      // QA is allowed on 2LR+ tiers (not 1LR)
      const taskTypeName = (this.taskTypeName || '').toLowerCase();
      const is1LR = taskTypeName.includes('1lr');
      if (is1LR || !taskTypeName) {
        return false;
      }

      // On QA-returned tasks, the coder responds; QA review is hidden
      if (this.isQAReturned) {
        return false;
      }

      if (this.activeChildren.length > 0) {
        const mostRecentQA = this.activeChildren[this.activeChildren.length - 1];
        const isLastQAByCurrentUser = mostRecentQA.appuser === this.currentUser?.id;

        if (isLastQAByCurrentUser && !this.isTenantAdmin && !this.isManagerViewer) {
          return false;
        }
      }

      return true;
    },

    canCoderRespond() {
      // No coder response on deleted codes
      if (this.isDeleted) return false;

      // Coder Response button appears only on QA-returned tasks, and only
      // on the code card whose original work is the one that got rejected.
      if (!this.isQAReturned) return false;

      // Only show on the original code (no parent) — QA review cards
      // already render as children via QACodeSuggestionDisplay.
      if (this.workItem.parent) return false;

      // Don't re-prompt if this coder has already responded on this code
      // (only count non-deleted responses)
      const hasOwnResponse = this.activeChildren.some(child => {
        const wt = (child.work_type_name || child.work_type_details?.name || '').toLowerCase();
        const isCoderResponse = wt.includes('coder_response') || wt.includes('coder response');
        return isCoderResponse && child.appuser === this.currentUser?.id;
      });
      return !hasOwnResponse;
    },

    isCoderResponseValid() {
      if (this.coderConcurrence === null) return false;
      if (this.coderConcurrence === false && !this.coderNotes.trim()) return false;
      return true;
    },

    allowDelete() {
      if (this.isDeleted) return false;
      if (this.workItem.appuser !== this.currentUser?.id) return false;
      return this.taskId == null || this.workItem.task === this.taskId;
    },

    statusOptions() {
      if (this.workflowType === 'encounter') {
        return [
          'Meat, Present',
          'Meat, Missing Status and Plan',
          'Meat, Missing Status',
          'Meat, Missing Plan'
        ];
      } else if (this.workflowType === 'suspect') {
        return ['Suspect Condition', 'Recapture Diagnosis'];
      } else if (this.workflowType === 'earlystart') {
        return ['Suspected Condition','Active Recapture','Confirmed Recapture'];
      }
      return [];
    },

    disagreeReasons() {
      return [
        'Clinical Indicator Insufficient',
        'Clinical Indicator linked to Another Diagnosis',
        'More Appropriate Code Identified',
        'Missed Suspect Code Opportunity (Remove)',
        'Administrative Error (Non-Coder Error)',
        'Data Error'
      ];
    },

    getAgreeText() {
      const name = this.taskTypeName.toLowerCase();
      if (name.includes('confirm')) {
        return 'Ready to Transmit';
      }
      return 'Confirm Code';
    },

    getDisagreeText() {
      const name = this.taskTypeName.toLowerCase();
      if (name.includes('confirm')) {
        return 'Not Valid for Transmit';
      }

      if (this.workflowType === 'encounter') {
        return this.taskTypeId === 8 ? 'Not Valid for Transmit' : 'Reject Code';
      }

      return 'Reject Code';
    },

    editRequiresExplanation() {
      if (this.workflowType === 'encounter') {
        return this.editedStatus !== 'Meat, Present';
      }
      return true;
    },

    isQAValid() {
      if (this.qaConcurrence === null) return false;

      if (this.qaConcurrence === false) {
        if (this.workflowType === 'suspect') {
          if (!this.qaDisagreeReason) return false;
          if (!this.qaExplanation.trim()) return false;
        }

        if (this.workflowType === 'encounter') {
          if (!this.qaStatus) return false;
          if (!this.qaExplanation.trim()) return false;
        }
      }

      return true;
    }
  },
  methods: {
    getStatusColorClass(status) {
      if (!status) return 'bg-gray-100 text-gray-700 border-gray-300';

      if (status === 'Meat, Present' || status === 'Suspect Condition') {
        return 'bg-green-100 text-green-700 border-green-300';
      } else if (status.includes('Missing') || status === 'Recapture Diagnosis') {
        return 'bg-amber-100 text-amber-700 border-amber-300';
      }

      return 'bg-gray-100 text-gray-700 border-gray-300';
    },

    toggleEditMode() {
      this.editMode = true;
      this.editedStatus = this.metadata.status;
      this.editedExplanation = this.metadata.explanation || '';
      this.editValidationError = '';
    },

    cancelEdit() {
      this.editMode = false;
      this.editedStatus = '';
      this.editedExplanation = '';
      this.editValidationError = '';
    },

    async saveEdits() {
      this.editValidationError = '';

      if (this.workflowType === 'encounter' &&
          this.editedStatus !== 'Meat, Present' &&
          (!this.editedExplanation || this.editedExplanation.trim() === '')) {
        this.editValidationError = 'Explanation is required when status is not "Meat, Present"';
        return;
      }

      if (this.workflowType === 'suspect' &&
          (!this.editedExplanation || this.editedExplanation.trim() === '')) {
        this.editValidationError = 'Clinical evidence is required';
        return;
      }

      this.saving = true;

      try {
        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.metadata,
            status: this.editedStatus,
            explanation: this.editedExplanation
          }
        });

        this.editMode = false;
        this.$emit('updated');
      } catch (error) {
        console.error('Error saving edits:', error);
        this.editValidationError = 'Failed to save changes';
      } finally {
        this.saving = false;
      }
    },

    async addImage(imageData) {
      this.uploadingImage = true;

      try {
        const updatedImages = [...(this.metadata.uploaded_images || [])];

        const newImage = {
          key: imageData.key,
          url: imageData.url,
          filename: imageData.filename,
          uploaded_by: {
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            created_at: new Date().toISOString()
          }
        };

        updatedImages.push(newImage);

        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.metadata,
            uploaded_images: updatedImages
          }
        });

        this.$emit('updated');
      } catch (error) {
        console.error('Error adding image:', error);
        alert('Failed to add image. Please try again.');
      } finally {
        this.uploadingImage = false;
      }
    },

    async removeImage(deletedKey) {
      this.uploadingImage = true;

      try {
        const updatedImages = (this.metadata.uploaded_images || []).filter(
          img => img.key !== deletedKey
        );

        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.metadata,
            uploaded_images: updatedImages
          }
        });

        this.$emit('updated');
      } catch (error) {
        console.error('Error removing image:', error);
        alert('Failed to remove image. Please try again.');
      } finally {
        this.uploadingImage = false;
      }
    },

    openQAForm() {
      this.showQAForm = true;
      this.qaConcurrence = null;
      this.qaDisagreeReason = '';
      this.qaStatus = this.metadata.status;
      this.qaExplanation = '';
      this.qaErrors = [];
    },

    cancelQA() {
      this.showQAForm = false;
      this.qaConcurrence = null;
      this.qaDisagreeReason = '';
      this.qaStatus = '';
      this.qaExplanation = '';
      this.qaErrors = [];
    },

    setQAResponse(value) {
      this.qaConcurrence = value;
      this.qaErrors = [];

      if (value === true) {
        this.submitQA();
      }
    },

    async submitQA() {
      this.qaErrors = [];

      if (this.qaConcurrence === false) {
        if (this.workflowType === 'suspect') {
          if (!this.qaDisagreeReason) {
            this.qaErrors.push('Please select a reason for disagreement');
          }
          if (!this.qaExplanation.trim()) {
            this.qaErrors.push('Explanation is required when disagreeing');
          }
        }

        if (this.workflowType === 'encounter') {
          if (!this.qaStatus) {
            this.qaErrors.push('Please select a status');
          }
          if (!this.qaExplanation.trim()) {
            this.qaErrors.push('Explanation is required when disagreeing');
          }
        }

        if (this.qaErrors.length > 0) {
          return;
        }
      }

      this.submitting = true;

      try {
        // Get opportunity ID from workItem - support both M2M (opportunities_list) and legacy FK (opportunity)
        const opportunityId = this.workItem.opportunities_list?.[0]?.id || this.workItem.opportunity;
        const qaData = {
          opportunity_ids: opportunityId ? [opportunityId] : [],  // M2M: link to opportunity
          opportunity: opportunityId,  // DEPRECATED: kept for backward compatibility
          work_type: 18,
          parent: this.workItem.id,
          appuser: this.currentUser?.id,
          task: this.taskId || null,
          entity: this.workItem.entity || null,
          metadata: {
            qa_concurrence: this.qaConcurrence,
            qa_disagree_reason: this.qaConcurrence === false && this.workflowType === 'suspect' ? this.qaDisagreeReason : null,
            qa_status: this.qaConcurrence === false && this.workflowType === 'encounter' ? this.qaStatus : this.metadata.status,
            qa_explanation: this.qaExplanation.trim() || null,
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            timestamp: new Date().toISOString(),
            parent_code: this.metadata.code,
            parent_status: this.metadata.status,
            workflow_type: this.workflowType
          }
        };

        const response = await axios.post('/api/work/', qaData);

        this.cancelQA();
        this.$emit('updated', response.data);

      } catch (error) {
        console.error('Error submitting QA:', error);
        this.qaErrors.push('Failed to submit QA review. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    openCoderResponseForm() {
      this.showCoderResponseForm = true;
      this.coderConcurrence = null;
      this.coderNotes = '';
      this.coderErrors = [];
    },

    cancelCoderResponse() {
      this.showCoderResponseForm = false;
      this.coderConcurrence = null;
      this.coderNotes = '';
      this.coderErrors = [];
    },

    setCoderConcurrence(value) {
      this.coderConcurrence = value;
      this.coderErrors = [];
    },

    async submitCoderResponse() {
      this.coderErrors = [];

      if (this.coderConcurrence === null) {
        this.coderErrors.push('Please choose Agree or Disagree');
        return;
      }
      if (this.coderConcurrence === false && !this.coderNotes.trim()) {
        this.coderErrors.push('Notes are required when disagreeing');
        return;
      }

      this.submitting = true;

      try {
        const opportunityId = this.workItem.opportunities_list?.[0]?.id || this.workItem.opportunity;
        const responseData = {
          opportunity_ids: opportunityId ? [opportunityId] : [],
          opportunity: opportunityId,
          work_type: 330,
          parent: this.workItem.id,
          appuser: this.currentUser?.id,
          task: this.taskId || null,
          entity: this.workItem.entity || null,
          metadata: {
            coder_concurrence: this.coderConcurrence,
            coder_notes: this.coderNotes.trim() || null,
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            timestamp: new Date().toISOString(),
            parent_code: this.metadata.code,
            parent_status: this.metadata.status,
            workflow_type: this.workflowType,
            qa_rejected_by: this.taskMetadata?.rejected_by ?? null,
            qa_rejection_reason: this.taskMetadata?.rejection_reason ?? null,
          }
        };

        const response = await axios.post('/api/work/', responseData);

        this.cancelCoderResponse();
        this.$emit('updated', response.data);
      } catch (error) {
        console.error('Error submitting coder response:', error);
        this.coderErrors.push('Failed to submit response. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    confirmDelete() {
      this.showDeleteConfirm = true;
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
    },

    async deleteCode() {
      this.deleting = true;

      try {
        await axios.delete(`/api/work/${this.workItem.id}/`);
        this.showDeleteConfirm = false;
        this.$emit('updated');
      } catch (error) {
        console.error('Error deleting code:', error);
        alert('Failed to delete code');
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
